import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchApi } from '../../api/search';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { recordSearchEntities } from './recordsSearchModel';
export default function useRecordSearch(query, profileId, enabled, composing = false) {
  const viewerId = useUserStore((s) => s.user?.id);
  const { locale } = useWebLocale();
  const term = query.trim().toLocaleLowerCase('ko-KR');
  const scope = JSON.stringify([viewerId ?? 'guest', profileId, locale]);
  const [ready, setReady] = useState(null);
  const eligible = enabled && !composing && term.length >= 2;
  const input = JSON.stringify([term, scope, eligible]);
  const [observedInput, setObservedInput] = useState(input);
  // Reset during render so A → B → A cannot reuse A before the new debounce expires.
  if (observedInput !== input) {
    setObservedInput(input);
    setReady(null);
  }
  useEffect(() => {
    if (!eligible) {
      setReady(null);
      return undefined;
    }
    const timer = setTimeout(() => setReady({ term, scope }), 320);
    return () => clearTimeout(timer);
  }, [eligible, term, scope]);
  const current =
    observedInput === input && eligible && ready?.term === term && ready?.scope === scope;
  const result = useQuery({
    queryKey: ['musicRecordsSearch', scope, term],
    enabled: !!current,
    queryFn: async ({ signal }) =>
      (
        await searchApi.searchSummary(term, {
          signal,
          authenticated: viewerId != null,
          params: { locale },
        })
      ).data,
    retry: false,
    staleTime: 10 * 60_000,
    gcTime: 0,
  });
  return {
    ...recordSearchEntities(current ? result.data : undefined),
    pending: eligible && (!current || result.isFetching),
  };
}
